import classNames from 'classnames';
import { Anchor, colourString, SunburstChart, useMatchScreenWidth } from '@keplerco/core';
import { SkillScore } from '../../../components/general/skill-score/skill-score';
import React from 'react';
import { UrgencyIndicationChip } from '../../../components/chips/urgency-indication.chip';
import SkillLevelHelp from '../../../components/general/skill-level-help/skill-level-help';
import { ScoreComparison } from '../../../enums/score-comparison';
import { comparisonScoreTitle } from '../../../library/helpers/comparison-score-title';
import { sortByUrgencyIndication } from '../../../pages/your-skills/your-skills.helpers';
import styles from './sunburst.module.css';
import { useAppState } from '../../../overmind';
import { ISunburstLayoutProps } from './sunburst.models';

export function SunburstLayout({ skillType, sunBurstData, isFirst, isLast, skillScoreUseRAGColours, chartTitle, skillScoreTitle, setSelectedSkillSubType, scoreComparison }: ISunburstLayoutProps) {
  const isMobile = useMatchScreenWidth('mobile');
  const { companyVariables } = useAppState();

  return (
    <div key={skillType.slug} className={classNames(styles.skillsReportContentTabSlide, { isFirst, isLast })}>
      <div className={classNames(styles.skillsReportCard, 'card')}>
        {!isMobile && (
          <div className={styles.skillsReportContentSunburst}>
            <SunburstChart data={sunBurstData} scale={5} />
          </div>
        )}

        <div className={styles.skillsReportContent}>
          <div className={styles.skillsReportContentHeader}>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <h3 id={skillType.slug}>{skillType.name}</h3>
              {!!companyVariables.useLevels && <SkillLevelHelp />}
            </div>

            {!skillType.skills[0].urgencyIndication && (
              <div className={styles.skillsReportContentScore}>
                <div className={styles.skillsReportContentLevel}>
                  <SkillScore score={skillType.score} title={skillScoreTitle} useRAGColours={skillScoreUseRAGColours} />
                </div>
              </div>
            )}
          </div>

          <div className={styles.skillsReportContentList}>
            <h4>{chartTitle}</h4>

            <div className={styles.skillItemList}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.td} style={{ color: 'var(--accent-2)' }}>
                      Skill name
                    </th>

                    {!!skillType.skills[0].urgencyIndication ? (
                      <th className={styles.td} />
                    ) : (
                      <React.Fragment>
                        <th className={styles.td}>{skillScoreTitle}</th>
                      </React.Fragment>
                    )}

                    {scoreComparison !== ScoreComparison.None && <th className={styles.td}>{comparisonScoreTitle(scoreComparison)}</th>}
                  </tr>
                </thead>

                <tbody>
                  {sunBurstData
                    .sort((a, b) => (!!a.urgencyIndication && !!b.urgencyIndication ? sortByUrgencyIndication(a.urgencyIndication, b.urgencyIndication) : a.value - b.value))
                    .slice(0, 4)
                    .map(slice => (
                      <tr key={slice.name}>
                        <td className={styles.td}>
                          <div className={styles.listItem}>
                            <span className={styles.listItemColor} style={{ background: colourString(slice.color) }} />
                            <span className={styles.listItemName}>{slice.name}</span>
                          </div>
                        </td>

                        {!!slice.urgencyIndication ? (
                          <td className={styles.td} style={{ textAlign: 'end' }}>
                            <UrgencyIndicationChip urgencyIndication={slice.urgencyIndication} />
                          </td>
                        ) : (
                          <React.Fragment>
                            <td className={styles.td}>
                              <SkillScore score={slice.score} useRAGColours={skillScoreUseRAGColours} />
                            </td>
                          </React.Fragment>
                        )}

                        {scoreComparison !== ScoreComparison.None && (
                          <td className={styles.td}>
                            <SkillScore score={slice.comparisonScore} useRAGColours={skillScoreUseRAGColours} noScoreText="Not captured" />
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.skillsReportContentActions}>
            <Anchor onClick={() => setSelectedSkillSubType(skillType)}>View Skills Breakdown</Anchor>
          </div>
        </div>
      </div>
    </div>
  );
}
